import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Stack, Chip, Select, MenuItem,
  CircularProgress, Tooltip, Button, Alert, Divider
} from "@mui/material";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const API = process.env.REACT_APP_API_URL || "http://localhost:8002";

const ROLE_COLORS = {
  fact: "#1565c0",
  dimension: "#2e7d32",
  aggregate: "#7e57c2",
};

export default function TableRoles({ connectionId = "oracle_free" }) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingTable, setSavingTable] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/oracle/roles/${connectionId}`)
      .then((r) => r.json())
      .then((data) => {
        setTables(data.tables || []);
        setLoading(false);
      })
      .catch((e) => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, [connectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const reclassify = async () => {
    setMsg("");
    setLoading(true);
    try {
      await fetch(`${API}/api/oracle/roles/${connectionId}/classify`, { method: "POST" });
      setMsg("Re-classified. Your manual overrides were preserved.");
      load();
    } catch (e) { setError(e.message); setLoading(false); }
  };

  const setRole = async (table, role) => {
    setSavingTable(table);
    try {
      await fetch(`${API}/api/oracle/roles/${connectionId}/override`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ table, role }),
      });
      setTables((prev) => prev.map((t) =>
        t.table === table ? { ...t, role, overridden: true } : t));
    } catch (e) { /* ignore */ }
    setSavingTable(null);
  };

  const clearOverride = async (table) => {
    setSavingTable(table);
    try {
      const res = await fetch(`${API}/api/oracle/roles/${connectionId}/clear-override`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ table, role: "dimension" }),
      });
      const data = await res.json();
      const updated = (data.tables || []).find((t) => t.table === table);
      if (updated) {
        setTables((prev) => prev.map((t) => t.table === table ? updated : t));
      }
    } catch (e) { /* ignore */ }
    setSavingTable(null);
  };

  if (loading)
    return (<Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={26} />
      <Typography sx={{ mt: 2, color: "#666" }}>Loading table roles…</Typography></Box>);
  if (error)
    return (<Box sx={{ p: 3 }}><Alert severity="warning">{error}</Alert></Box>);

  const facts = tables.filter((t) => t.role === "fact").length;
  const dims = tables.filter((t) => t.role === "dimension").length;
  const aggs = tables.filter((t) => t.role === "aggregate").length;

  return (
    <Box sx={{ bgcolor: "#eceff1", minHeight: "100vh", py: 3 }}>
    <Box sx={{ p: 3, maxWidth: 920, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <FactCheckIcon sx={{ color: "#1565c0" }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Table Roles</Typography>
        <Chip size="small" variant="outlined" label={`${facts} facts`} sx={{ color: ROLE_COLORS.fact }} />
        <Chip size="small" variant="outlined" label={`${dims} dimensions`} sx={{ color: ROLE_COLORS.dimension }} />
        <Chip size="small" variant="outlined" label={`${aggs} aggregates`} sx={{ color: ROLE_COLORS.aggregate }} />
      </Stack>
      <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
        Roles are auto-detected from your data. Change any role using the dropdown —
        your choice is saved and always used, even if classification is re-run.
      </Typography>

      {msg && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}

      <Button size="small" variant="outlined" startIcon={<RestartAltIcon />}
        onClick={reclassify} sx={{ mb: 2 }}>
        Re-run auto-classification
      </Button>

      <Paper variant="outlined" sx={{ p: 0 }}>
        {tables.map((t, i) => (
          <Box key={t.table}>
            <Stack direction="row" alignItems="center" spacing={2}
              sx={{ px: 2, py: 1, bgcolor: i % 2 === 0 ? "#f5f5f5" : "#ffffff" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                  {t.table}
                  {t.overridden && (
                    <Chip size="small" label="manual" sx={{ ml: 1, height: 16, fontSize: 10 }} />
                  )}
                </Typography>
                <Typography variant="caption" sx={{ color: "#999" }}>
                  {t.rows?.toLocaleString()} rows · {t.why}
                </Typography>
              </Box>

              <Select size="small" value={t.role}
                onChange={(e) => setRole(t.table, e.target.value)}
                disabled={savingTable === t.table}
                sx={{ minWidth: 130, height: 32,
                      color: ROLE_COLORS[t.role] || "inherit", fontWeight: 600 }}>
                <MenuItem value="fact">fact</MenuItem>
                <MenuItem value="dimension">dimension</MenuItem>
                <MenuItem value="aggregate">aggregate</MenuItem>
              </Select>

              {t.overridden && (
                <Tooltip title="Reset to auto-detected role">
                  <Button size="small" onClick={() => clearOverride(t.table)}
                    disabled={savingTable === t.table}>reset</Button>
                </Tooltip>
              )}
            </Stack>
            {i < tables.length - 1 && <Divider />}
          </Box>
        ))}
      </Paper>
    </Box>
    </Box>
  );
}
